'use client'

import { atom } from 'jotai'
import { atomFamily } from 'jotai-family'
import { atomWithStorage } from 'jotai/utils'

import { SUPPORTED_EXCHANGE } from '@/lib/configs/exchange-config'
import { exchangeToTradeVenue } from '@/lib/configs/exchange-registry'
import { STORAGE_KEYS } from '@/lib/configs/storage-config'
import store from '@/lib/configs/store-config'
import { toExchangeEnum } from '@/lib/proto/util'
import { namespacedKey, safeStorage } from '@/lib/safe-storage'
import { chartSelectedAccountIdAtomFamily } from '@/state/chart-account'
import { ChartAnalyticsOption, ChartOption, PositionFilter } from '@/types/dashboard'
import { IncrementalTicker, TradeVenue } from '@/types/generated/common/types'

import { LinkingColor, linkingColorsAtomFamily, LinkingTickerInfo } from './linking'
import { WatchList } from './storage'

/**
 * Persisted override set by an option-chain widget when a row is clicked.
 * Takes priority over selectedTicker / watchList for symbol resolution in linked widgets.
 * Cleared when the originating option-chain widget is removed from the layout.
 */
export type OptionChainOverride = {
  sourceCardId: string
  symbol: string
  type: 'options'
  exchange: keyof typeof SUPPORTED_EXCHANGE
  baseCurrency: string
  expiry?: string
  pricePrecision?: string
  strikePrice?: string
  optionType?: 'call' | 'put'
}

/**
 * Complete state for a single linking group
 * Each color group (mint, yellow, orange, purple, green) has its own isolated state
 * Unlinked cards have per-card isolated state stored separately
 */
export interface LinkingGroupState {
  // Tab collection for the linking group.
  // `watchList.focusedTabId` is the canonical focused tab owner; `selectedIndex`
  // is kept as a legacy mirror for existing tab components.
  watchList: WatchList

  // Current linked symbol intent for this group.
  // Non-option tab clicks usually set the focused chart symbol here.
  // Option row clicks may also set this to a full option contract, but linked
  // widgets should read `effectiveTicker` from useLinkingGroupState so
  // optionChainOverride priority is applied consistently.
  selectedTicker: LinkingTickerInfo | null

  // Chart configuration (timeframe, scale)
  chartOption: ChartOption

  // Option chain state.
  // selectedOptionTicker is the option table's underlying group, e.g. BTC or ETH_USDC.
  // It is intentionally not a full option contract and should not be treated as
  // the active linked symbol for orderbook/trades streams.
  selectedOptionTicker: string | null
  selectedOptionExpiry: string | null
  // optionRows and optionUnderlyingPrice live in their own atomFamilies
  // (optionRowsAtomFamily / optionUnderlyingPriceAtomFamily below), NOT here —
  // see those atoms' doc comments for why.

  // Position Filter (Group-isolated)
  positionFilter: PositionFilter

  // Focused strike for option table auto-scroll (set when clicking from position/order tables)
  // _id is a monotonically increasing counter so each click triggers a new scroll
  focusedOptionStrike: { strike: number; expiry: string; ticker: string; _id: number } | null

  // Full option-contract override set by an option-chain widget when a row is clicked.
  // When non-null, linked market-data widgets should use this ahead of
  // selectedTicker/watchList. It carries sourceCardId for cleanup and metadata
  // such as pricePrecision that selectedTicker does not preserve.
  optionChainOverride: OptionChainOverride | null
}

const createAnalyticsOption = (
  placement: 'overlay' | 'pane',
  suite:
    | 'price-overlays'
    | 'advanced-volume-profile'
    | 'order-flow-delta'
    | 'liquidation-map'
    | 'market-microstructure'
    | 'volatility-spreads'
    | 'derivatives'
    | 'cross-exchange',
  category:
    | 'price'
    | 'volume'
    | 'liquidations'
    | 'microstructure'
    | 'volatility'
    | 'derivatives'
    | 'cross-exchange',
  enabled = false
) => ({
  enabled,
  placement,
  suite,
  category,
})

export const DEFAULT_CHART_ANALYTICS: ChartAnalyticsOption = {
  vwap: createAnalyticsOption('overlay', 'price-overlays', 'price', true),
  twap: createAnalyticsOption('overlay', 'price-overlays', 'price'),
  liqwap: createAnalyticsOption('overlay', 'price-overlays', 'price'),

  volumeBase: createAnalyticsOption('pane', 'advanced-volume-profile', 'volume'),
  volumeQuote: createAnalyticsOption('pane', 'advanced-volume-profile', 'volume'),
  buyVolumeBase: createAnalyticsOption('pane', 'advanced-volume-profile', 'volume'),
  sellVolumeBase: createAnalyticsOption('pane', 'advanced-volume-profile', 'volume'),
  netBuyVolumeBase: createAnalyticsOption('pane', 'order-flow-delta', 'volume'),
  cumulativeVolumeDelta: createAnalyticsOption('pane', 'order-flow-delta', 'volume'),
  netBuyVolumeQuote: createAnalyticsOption('pane', 'order-flow-delta', 'volume'),
  deltaPercentage: createAnalyticsOption('pane', 'order-flow-delta', 'volume'),

  totalLiqBase: createAnalyticsOption('pane', 'liquidation-map', 'liquidations'),
  totalLiqQuote: createAnalyticsOption('pane', 'liquidation-map', 'liquidations'),
  buySellLiq: createAnalyticsOption('pane', 'liquidation-map', 'liquidations'),
  liqTakerVolume: createAnalyticsOption('pane', 'liquidation-map', 'liquidations'),
  liqMakerVolume: createAnalyticsOption('pane', 'liquidation-map', 'liquidations'),
  totalLiqCount: createAnalyticsOption('pane', 'liquidation-map', 'liquidations'),
  netLiquidations: createAnalyticsOption('pane', 'liquidation-map', 'liquidations'),

  totalTradeCount: createAnalyticsOption('pane', 'market-microstructure', 'microstructure'),
  buyTradeCount: createAnalyticsOption('pane', 'market-microstructure', 'microstructure'),
  sellTradeCount: createAnalyticsOption('pane', 'market-microstructure', 'microstructure'),
  netBuyTradeCount: createAnalyticsOption('pane', 'market-microstructure', 'microstructure'),
  avgTradeSize: createAnalyticsOption('pane', 'market-microstructure', 'microstructure'),
  avgBuyTradeSize: createAnalyticsOption('pane', 'market-microstructure', 'microstructure'),
  avgSellTradeSize: createAnalyticsOption('pane', 'market-microstructure', 'microstructure'),

  parkinsonVolatility: createAnalyticsOption('pane', 'volatility-spreads', 'volatility'),
  vwapTwapSpread: createAnalyticsOption('pane', 'volatility-spreads', 'volatility'),

  volumeBaseBlockIncl: createAnalyticsOption('pane', 'advanced-volume-profile', 'volume'),
  volumeQuoteBlockIncl: createAnalyticsOption('pane', 'advanced-volume-profile', 'volume'),
  buyVolumeBaseBlockIncl: createAnalyticsOption('pane', 'advanced-volume-profile', 'volume'),
  sellVolumeBaseBlockIncl: createAnalyticsOption('pane', 'advanced-volume-profile', 'volume'),
  netBuyVolumeBaseBlockIncl: createAnalyticsOption('pane', 'advanced-volume-profile', 'volume'),
  netBuyVolumeQuoteBlockIncl: createAnalyticsOption('pane', 'advanced-volume-profile', 'volume'),

  openInterestBase: createAnalyticsOption('pane', 'derivatives', 'derivatives'),
  openInterestQuote: createAnalyticsOption('pane', 'derivatives', 'derivatives'),
  aggregatedOpenInterest: createAnalyticsOption('pane', 'derivatives', 'derivatives'),
  fundingRate: createAnalyticsOption('pane', 'derivatives', 'derivatives'),
  aggregatedFundingRateAvgClose: createAnalyticsOption('pane', 'derivatives', 'derivatives'),
  fundingApr: createAnalyticsOption('pane', 'derivatives', 'derivatives'),
  putCallRatioOi: createAnalyticsOption('pane', 'derivatives', 'derivatives'),
  putCallRatioVolume: createAnalyticsOption('pane', 'derivatives', 'derivatives'),
  optionsSkew25d: createAnalyticsOption('pane', 'derivatives', 'derivatives'),
  optionsVolume: createAnalyticsOption('pane', 'derivatives', 'derivatives'),

  obSpreadBps: createAnalyticsOption('pane', 'market-microstructure', 'microstructure'),
  obPressureRatio: createAnalyticsOption('pane', 'market-microstructure', 'microstructure'),
  obBidLiquidityTotal: createAnalyticsOption('pane', 'market-microstructure', 'microstructure'),
  obAskLiquidityTotal: createAnalyticsOption('pane', 'market-microstructure', 'microstructure'),
  obDepthImbalance: createAnalyticsOption('pane', 'market-microstructure', 'microstructure'),

  crossExchangeOpenInterest: createAnalyticsOption('pane', 'cross-exchange', 'cross-exchange'),
  crossExchangeFundingRate: createAnalyticsOption('pane', 'cross-exchange', 'cross-exchange'),
  crossExchangeCvd: createAnalyticsOption('pane', 'cross-exchange', 'cross-exchange'),
}

/**
 * Default empty state for new groups
 */
export const DEFAULT_GROUP_STATE: LinkingGroupState = {
  watchList: { list: [], focusedTabId: null, selectedIndex: -1 },
  selectedTicker: null,

  // Chart defaults
  chartOption: {
    timeframe: 60 * 60,
    scale: 'Normal',
    chartType: 1,
    analytics: DEFAULT_CHART_ANALYTICS,
    showPositionLine: true,
    showLiquidationLine: true,
    showBuySellTriangles: true,
    showBuySellButtons: true,
    showOpenOrders: true,
    showIndex: true,
    showTopBar: true,
    showHighLow: false,
    autoScale: true,
    volumeDeltaProfileEnabled: false,
    liquidationProfileEnabled: false,
    openInterestProfileEnabled: false,
    volumeDeltaProfileUnit: 'quote',
    liquidationProfileUnit: 'quote',
    openInterestProfileUnit: 'quote',
    visibleRangeProfileUnit: 'quote',
    liquidationLevelProfileEnabled: false,
    stopLevelProfileEnabled: false,
    liquidationLevelProfileMeasure: 'notional',
    stopLevelProfileMeasure: 'notional',
    volumeDeltaProfileShowLabels: true,
    liquidationProfileShowLabels: true,
    openInterestProfileShowLabels: true,
    liquidationLevelProfileShowLabels: true,
    stopLevelProfileShowLabels: true,
    orderbookHeatmapEnabled: false,
  },

  // Option chain defaults
  selectedOptionTicker: null,
  selectedOptionExpiry: null,

  // Position filter default
  positionFilter: {
    instruments: { disabled: false, value: ['ALL'] },
    expiries: { disabled: false, value: ['ALL'] },
    assets: { disabled: false, value: ['ALL'] },
    accounts: { disabled: false, value: ['ALL'] },
    size: { disabled: false, value: null },
    pnl: { disabled: false, value: null },
    strikeRange: { disabled: false, value: null },
    positionDelta: { disabled: false, value: null },
  },

  // Focused strike default
  focusedOptionStrike: null,

  // Option chain override default
  optionChainOverride: null,
}

/**
 * State keyed by LinkingColor (group identifier)
 */
export type LinkingGroupStateMap = Record<LinkingColor, LinkingGroupState>

/**
 * Default state for all color groups
 */
export const DEFAULT_GROUP_STATE_MAP: LinkingGroupStateMap = {
  unlinked: DEFAULT_GROUP_STATE, // Placeholder, actual unlinked state is per-card
  mint: { ...DEFAULT_GROUP_STATE },
  yellow: { ...DEFAULT_GROUP_STATE },
  orange: { ...DEFAULT_GROUP_STATE },
  purple: { ...DEFAULT_GROUP_STATE },
  blue: { ...DEFAULT_GROUP_STATE },
  pink: { ...DEFAULT_GROUP_STATE },
  green: { ...DEFAULT_GROUP_STATE },
}

/**
 * True once a page's link-group tabs have been restored from the server (DataManagerSync), or
 * found to have nothing to restore. Until then the tabs are the localStorage/default copy,
 * which a reload can still replace — the Order Form's one-time seed waits for this so it seeds
 * from the tab the page actually lands on.
 */
export const linkingGroupsRestoredAtomFamily = atomFamily((_pageId: string) => atom(false))

/**
 * Per-page grouped state atom.
 * Stores state for all color groups, isolated per page.
 * Key: pageId (from LinkingPageContext), Value: state map per color
 */
export const linkingGroupStateAtomFamily = atomFamily((pageId: string) =>
  atomWithStorage<LinkingGroupStateMap>(
    `${STORAGE_KEYS.dashboard.linkingGroups}-${pageId}`,
    DEFAULT_GROUP_STATE_MAP,
    safeStorage,
    { getOnInit: true }
  )
)

/**
 * Per-page state for unlinked cards.
 * Each unlinked card has fully isolated state, scoped to its page.
 * Key: cardId, Value: LinkingGroupState
 */
export const unlinkedCardStateAtomFamily = atomFamily((pageId: string) =>
  atomWithStorage<Record<string, LinkingGroupState>>(
    `${STORAGE_KEYS.dashboard.unlinkedCards}-${pageId}`,
    {},
    safeStorage,
    { getOnInit: true }
  )
)

/**
 * Scope key for the per-chart-widget settings/timeframe atoms in
 * state/widget-instances.ts. Widget IDs are only unique *per page* for the
 * legacy counter-based ID scheme (e.g. 'price-chart-1' can exist on two
 * different pages) — see state/dashboard-layouts.ts — so a flat cardId-keyed
 * atomFamily would collide across pages. Newer IDs use a random suffix and
 * are effectively globally unique, but old ones must still be supported.
 */
export function chartWidgetScopeKey(pageId: string, cardId: string): string {
  return `${pageId}:${cardId}`
}

/**
 * Scope key for the ephemeral option-chain atoms below (rows + underlying price) —
 * same linked-vs-unlinked routing useLinkingGroupState itself uses (shared per
 * linking color when linked, isolated per card when unlinked), reproduced here
 * since those atoms live outside LinkingGroupState and can't just piggyback on
 * setState's routing.
 */
export function optionRowsScopeKey(
  pageId: string,
  linkingColor: LinkingColor,
  cardId: string
): string {
  return linkingColor === 'unlinked' ? `${pageId}:unlinked:${cardId}` : `${pageId}:${linkingColor}`
}

/**
 * Live option-chain ticker rows (strike -> call/put), keyed by optionRowsScopeKey.
 * Deliberately a plain atom, NOT atomWithStorage: this updates on every option-chain
 * WS tick (multiple times a second while an option chain is open), and it used to
 * live inside LinkingGroupState — which IS atomWithStorage-backed. Every write to a
 * persisted atom notifies all of that atom's subscribers and re-arms safe-storage's
 * debounce, eventually serializing all 7 linking colors' entire state (watchList,
 * chart analytics settings, position filters, ...) — just to persist live prices
 * nobody needs restored after a reload. Shared across a linking color's widgets (not
 * per-widget like chartOptionAtomFamily) because the order form's strike-price
 * dropdown reads a sibling option-chain widget's rows via this same key.
 */
export const optionRowsAtomFamily = atomFamily((_scopeKey: string) =>
  atom<Map<number, { call?: IncrementalTicker; put?: IncrementalTicker }>>(new Map())
)

/**
 * Live underlying (index) price for an option chain, keyed by optionRowsScopeKey.
 *
 * Split out of LinkingGroupState for exactly the reason optionRowsAtomFamily above
 * was: it's written from the same WS handler at the same per-batch frequency
 * (use-option-chain-table.ts's handleTickers), so leaving it in the persisted,
 * group-shared state meant every price tick allocated a new LinkingGroupState,
 * re-rendered every consumer of that slice — including widgets that never read the
 * price — and re-armed a localStorage write for a value that is meaningless after a
 * reload. Consumers should read it through useLinkingGroupOptionUnderlyingPrice.
 */
export const optionUnderlyingPriceAtomFamily = atomFamily((_scopeKey: string) =>
  atom<number | null>(null)
)

const LINKED_OPTION_ROW_COLORS: Exclude<LinkingColor, 'unlinked'>[] = [
  'mint',
  'yellow',
  'orange',
  'pink',
  'purple',
  'blue',
  'green',
]

/**
 * An unlinked option-chain scope belongs to exactly one widget, so it can be
 * evicted as soon as that widget is deleted or joins a linked color. Covers every
 * ephemeral atom keyed by optionRowsScopeKey, not just the rows.
 */
export function cleanupUnlinkedOptionRows(pageId: string, cardId: string) {
  const scopeKey = optionRowsScopeKey(pageId, 'unlinked', cardId)
  optionRowsAtomFamily.remove(scopeKey)
  optionUnderlyingPriceAtomFamily.remove(scopeKey)
}

/**
 * Evict every ephemeral option-chain atom owned by a deleted page. Linked scopes
 * are a fixed set; unlinked scopes are discovered before the page atoms below
 * are removed.
 */
export function cleanupOptionRowsForPage(pageId: string) {
  const linkingColors = store.get(linkingColorsAtomFamily(pageId))
  const unlinkedStates = store.get(unlinkedCardStateAtomFamily(pageId))
  const cardIds = new Set([...Object.keys(linkingColors), ...Object.keys(unlinkedStates)])

  for (const cardId of cardIds) {
    cleanupUnlinkedOptionRows(pageId, cardId)
  }
  for (const color of LINKED_OPTION_ROW_COLORS) {
    const scopeKey = optionRowsScopeKey(pageId, color, '')
    optionRowsAtomFamily.remove(scopeKey)
    optionUnderlyingPriceAtomFamily.remove(scopeKey)
  }
}

/**
 * Legacy fallback read for a chart widget's ChartOption, from BEFORE settings
 * were split into their own per-widget atom (chartOptionAtomFamily in
 * state/widget-instances.ts). Used only as a one-time lazy-migration seed:
 * once a widget's chartOption is written to its own atom, this is never
 * consulted again for that widget.
 */
export function resolveLegacyChartOption(
  pageId: string,
  cardId: string,
  linkingColor: LinkingColor
): ChartOption | undefined {
  return linkingColor === 'unlinked'
    ? store.get(unlinkedCardStateAtomFamily(pageId))[cardId]?.chartOption
    : store.get(linkingGroupStateAtomFamily(pageId))[linkingColor]?.chartOption
}

/**
 * Legacy fallback read for a single tab's timeframe, from BEFORE timeframe
 * was split into its own per-widget atom (chartTabTimeframeAtomFamily).
 * Same one-time lazy-migration-seed role as resolveLegacyChartOption.
 */
export function resolveLegacyTabTimeframe(
  pageId: string,
  cardId: string,
  linkingColor: LinkingColor,
  tabId: string
): number | undefined {
  const watchList =
    linkingColor === 'unlinked'
      ? store.get(unlinkedCardStateAtomFamily(pageId))[cardId]?.watchList
      : store.get(linkingGroupStateAtomFamily(pageId))[linkingColor]?.watchList
  return watchList?.list.find((item) => item.id === tabId)?.timeframe
}

/**
 * Pins `accountId` onto every open tab — across every linking color group AND
 * every unlinked card on the page — whose own venue matches `tradeVenue`, so
 * every widget on the page consistently trades through the same account for
 * that venue. Tabs on other venues are left untouched (an account only ever
 * belongs to one venue, so there's nothing meaningful to align there). Updates
 * every tab in each watchList, not just the currently focused one, so the
 * alignment survives the user later switching which tab is focused.
 *
 * Also points every group/card's own position-table account filter
 * (`positionFilter.accounts`) at this account — unlike the tab pin above,
 * this isn't venue-scoped: a position table can already show cross-account/
 * cross-venue rows, so "align to this account" means filter every position
 * table down to just it, regardless of what venue its own chart tabs happen
 * to be on.
 *
 * Widgets with their own independent account state outside LinkingGroupState
 * (e.g. the Account Cards widget's `accountCardsSelectedAccountIdAtomFamily`)
 * are NOT covered here — see dashboard-context-menu.tsx's caller, which
 * additionally walks the page's own widget list for those.
 */
export const accountAlignmentSelectionAtomFamily = atomFamily((_pageId: string) =>
  atom<{ accountId: string; revision: number } | null>(null)
)

export const accountAlignmentRevisionAtomFamily = atomFamily((_pageId: string) =>
  atom<Partial<Record<TradeVenue, number>>>({})
)

export function alignPageTabsToAccount(pageId: string, tradeVenue: TradeVenue, accountId: string) {
  store.set(accountAlignmentRevisionAtomFamily(pageId), (prev) => ({
    ...prev,
    [tradeVenue]: (prev[tradeVenue] ?? 0) + 1,
  }))
  store.set(accountAlignmentSelectionAtomFamily(pageId), (prev) => ({
    accountId,
    revision: (prev?.revision ?? 0) + 1,
  }))
  const alignState = (state: LinkingGroupState): LinkingGroupState => {
    let changed = false

    let nextWatchList = state.watchList
    const list = state.watchList.list.map((item) => {
      if (exchangeToTradeVenue(toExchangeEnum(item.exchange)) !== tradeVenue) return item
      if (item.accountId === accountId) return item
      changed = true
      return { ...item, accountId }
    })
    if (changed) nextWatchList = { ...state.watchList, list }

    let nextPositionFilter = state.positionFilter
    const currentAccountsFilter = state.positionFilter.accounts
    if (currentAccountsFilter.disabled || currentAccountsFilter.value.join(',') !== accountId) {
      changed = true
      nextPositionFilter = {
        ...state.positionFilter,
        accounts: { disabled: false, value: [accountId] },
      }
    }

    return changed
      ? { ...state, watchList: nextWatchList, positionFilter: nextPositionFilter }
      : state
  }

  const groupStates = store.get(linkingGroupStateAtomFamily(pageId))
  const nextGroupStates = { ...groupStates }
  let groupsChanged = false
  for (const color of Object.keys(groupStates) as LinkingColor[]) {
    const state = groupStates[color]
    if (!state) continue
    const nextState = alignState(state)
    if (nextState !== state) {
      nextGroupStates[color] = nextState
      groupsChanged = true
    }
  }
  if (groupsChanged) store.set(linkingGroupStateAtomFamily(pageId), nextGroupStates)

  const unlinkedStates = store.get(unlinkedCardStateAtomFamily(pageId))
  const nextUnlinkedStates = { ...unlinkedStates }
  let unlinkedChanged = false
  for (const cardId of Object.keys(unlinkedStates)) {
    const state = unlinkedStates[cardId]
    const nextState = alignState(state)
    if (nextState !== state) {
      nextUnlinkedStates[cardId] = nextState
      unlinkedChanged = true
    }
  }
  if (unlinkedChanged) store.set(unlinkedCardStateAtomFamily(pageId), nextUnlinkedStates)

  // Explicit chart picks outrank tab pins. Clear them for cards whose tabs were
  // aligned, otherwise both chart engines keep showing the previously picked account.
  const colors = store.get(linkingColorsAtomFamily(pageId))
  const cardIds = new Set([...Object.keys(colors), ...Object.keys(unlinkedStates)])
  for (const cardId of cardIds) {
    const color = colors[cardId] ?? 'mint'
    const state = color === 'unlinked' ? unlinkedStates[cardId] : groupStates[color]
    const tab =
      state?.watchList.list.find((item) => item.id === state.watchList.focusedTabId) ??
      state?.watchList.list[state.watchList.selectedIndex]
    if (tab && exchangeToTradeVenue(toExchangeEnum(tab.exchange)) === tradeVenue) {
      store.set(chartSelectedAccountIdAtomFamily(cardId), null)
    }
  }
}

/**
 * Clean up atom instances and storage for a deleted page.
 * Call this when a page tab is removed.
 */
export function cleanupLinkingPageState(pageId: string) {
  cleanupOptionRowsForPage(pageId)
  accountAlignmentRevisionAtomFamily.remove(pageId)
  accountAlignmentSelectionAtomFamily.remove(pageId)
  linkingGroupStateAtomFamily.remove(pageId)
  unlinkedCardStateAtomFamily.remove(pageId)
  linkingColorsAtomFamily.remove(pageId)
  if (typeof window !== 'undefined') {
    window.localStorage?.removeItem(
      namespacedKey(`${STORAGE_KEYS.dashboard.linkingGroups}-${pageId}`)
    )
    window.localStorage?.removeItem(
      namespacedKey(`${STORAGE_KEYS.dashboard.unlinkedCards}-${pageId}`)
    )
  }
}
